import { useState } from 'react'
import { Copy, Search, Loader2, CheckCircle2, XCircle, GitMerge } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useDuplicates,
  useDuplicateStats,
  useScanDuplicates,
  useResolveDuplicate,
} from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { MergeRecordsModal } from '@/components/MergeRecordsModal'
import type { DuplicateMatch, DuplicateEntityType } from '@/types'
import { GRACKLE_COLORS, SEMANTIC, CATEGORICAL } from '@/lib/theme'

const confidenceBadgeStyles: Record<
  string,
  { backgroundColor: string; color: string; borderColor: string }
> = {
  High: {
    backgroundColor: SEMANTIC.error.fill,
    color: SEMANTIC.error.text,
    borderColor: CATEGORICAL.raspberry.solid,
  },
  Medium: {
    backgroundColor: SEMANTIC.warning.fill,
    color: SEMANTIC.warning.text,
    borderColor: CATEGORICAL.pollen.solid,
  },
  Low: {
    backgroundColor: SEMANTIC.neutral.fill,
    color: SEMANTIC.neutral.text,
    borderColor: CATEGORICAL.slate.solid,
  },
}

const statusBadgeStyles: Record<string, { backgroundColor: string; color: string }> = {
  pending: {
    backgroundColor: CATEGORICAL.clay.tint,
    color: GRACKLE_COLORS.prussianBlue,
  },
  resolved: {
    backgroundColor: SEMANTIC.success.fill,
    color: SEMANTIC.success.text,
  },
  dismissed: {
    backgroundColor: SEMANTIC.neutral.fill,
    color: SEMANTIC.neutral.text,
  },
}

export default function Duplicates() {
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<string>('All')
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [confidenceFilter, setConfidenceFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const limit = 25

  // Merge modal state
  const [mergeModalOpen, setMergeModalOpen] = useState(false)
  const [mergeEntityType, setMergeEntityType] = useState<DuplicateEntityType>('Account')
  const [mergeRecordA, setMergeRecordA] = useState('')
  const [mergeRecordB, setMergeRecordB] = useState('')
  const [mergeDuplicateId, setMergeDuplicateId] = useState<string | undefined>(undefined)

  const { data: statsData } = useDuplicateStats()
  const stats = statsData

  const { data: duplicatesData, isLoading } = useDuplicates(activeTab, {
    page,
    limit,
    status: statusFilter || undefined,
    confidence_level: confidenceFilter || undefined,
  })

  const scanMutation = useScanDuplicates()
  const resolveMutation = useResolveDuplicate()

  const duplicates: DuplicateMatch[] = duplicatesData?.data || []
  const total = duplicatesData?.total || 0
  const totalPages = Math.ceil(total / limit)

  const handleScan = async (entityType: string) => {
    try {
      const result = await scanMutation.mutateAsync(entityType)
      toast({
        title: 'Scan Complete',
        description: `Scanned ${result.scannedCount} ${entityType.toLowerCase()}s. Found ${result.matchesFound} potential duplicates.`,
      })
    } catch {
      toast({
        title: 'Scan Failed',
        description: 'An error occurred during the scan.',
        variant: 'destructive',
      })
    }
  }

  const handleResolve = async (id: string, resolution: 'not_duplicate' | 'dismissed') => {
    try {
      await resolveMutation.mutateAsync({ id, resolution })
      toast({
        title: 'Resolved',
        description:
          resolution === 'not_duplicate' ? 'Marked as not a duplicate.' : 'Duplicate dismissed.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to resolve duplicate.',
        variant: 'destructive',
      })
    }
  }

  const handleMerge = (match: DuplicateMatch) => {
    setMergeEntityType(
      match.entity_type === 'CrossEntity' ? 'Contact' : (match.entity_type as DuplicateEntityType)
    )
    setMergeRecordA(match.record_id_a)
    setMergeRecordB(match.record_id_b)
    setMergeDuplicateId(match.id)
    setMergeModalOpen(true)
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    setPage(1)
  }

  const getSignalSummary = (match: DuplicateMatch) => {
    return match.match_signals.map((s) => s.signal.replace(/_/g, ' ')).join(', ')
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Copy className="h-8 w-8 text-gray-600" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-bold">Duplicate Management</h1>
            <p className="text-sm text-muted-foreground">
              Detect, review, and merge duplicate records
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total_pending}</div>
              <p className="text-xs text-muted-foreground">Total Pending</p>
            </CardContent>
          </Card>
          {(['accounts', 'contacts', 'leads', 'crossEntity'] as const).map((key) => (
            <Card key={key}>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{stats[key].pending}</div>
                <p className="text-xs text-muted-foreground capitalize">
                  {key === 'crossEntity' ? 'Cross-Entity' : key}
                  {stats[key].high > 0 && (
                    <Badge
                      variant="outline"
                      className="ml-2 text-[10px]"
                      style={{
                        backgroundColor: CATEGORICAL.raspberry.tint,
                        color: CATEGORICAL.raspberry.solid,
                        borderColor: CATEGORICAL.raspberry.solid,
                      }}
                    >
                      {stats[key].high} high
                    </Badge>
                  )}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle>Duplicate Matches</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Scan Buttons */}
              {(['Account', 'Contact', 'Lead'] as const).map((type) => (
                <Button
                  key={type}
                  size="sm"
                  variant="outline"
                  onClick={() => handleScan(type)}
                  disabled={scanMutation.isPending}
                  aria-label={`Scan ${type}s for duplicates`}
                >
                  {scanMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden="true" />
                  ) : (
                    <Search className="h-4 w-4 mr-1" aria-hidden="true" />
                  )}
                  Scan {type}s
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
              <TabsList role="tablist" aria-label="Filter by entity type">
                <TabsTrigger value="All">All</TabsTrigger>
                <TabsTrigger value="Account">Accounts</TabsTrigger>
                <TabsTrigger value="Contact">Contacts</TabsTrigger>
                <TabsTrigger value="Lead">Leads</TabsTrigger>
                <TabsTrigger value="CrossEntity">Cross-Entity</TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-2">
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger className="w-[140px]" aria-label="Filter by status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={confidenceFilter}
                  onValueChange={(v) => {
                    setConfidenceFilter(v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger className="w-[140px]" aria-label="Filter by confidence">
                    <SelectValue placeholder="Confidence" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Content for all tabs uses the same list */}
            {['All', 'Account', 'Contact', 'Lead', 'CrossEntity'].map((tab) => (
              <TabsContent key={tab} value={tab} role="tabpanel">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12" role="status">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden="true" />
                    <span className="sr-only">Loading duplicates...</span>
                  </div>
                ) : duplicates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2
                      className="h-12 w-12 mx-auto mb-4"
                      style={{ color: CATEGORICAL.evergreen.solid }}
                      aria-hidden="true"
                    />
                    <p className="text-lg font-medium">No duplicates found</p>
                    <p className="text-sm mt-1">
                      Use the Scan buttons above to check for duplicates.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {duplicates.map((match) => (
                      <div
                        key={match.id}
                        className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        role="article"
                        aria-label={`Duplicate match: ${match.record_a_name || match.record_id_a} and ${match.record_b_name || match.record_id_b}`}
                      >
                        {/* Match Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant="outline" className="text-xs capitalize">
                              {match.entity_type === 'CrossEntity'
                                ? 'Lead ↔ Contact'
                                : match.entity_type}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="text-xs"
                              style={confidenceBadgeStyles[match.confidence_level] || {}}
                            >
                              {match.confidence_score}% {match.confidence_level}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="text-xs"
                              style={statusBadgeStyles[match.status] || {}}
                            >
                              {match.status}
                            </Badge>
                          </div>
                          <p className="text-sm font-medium">
                            {match.record_a_name || match.record_id_a} —{' '}
                            {match.record_b_name || match.record_id_b}
                          </p>
                          {(match.record_a_email || match.record_b_email) && (
                            <p className="text-xs text-muted-foreground">
                              {[match.record_a_email, match.record_b_email]
                                .filter(Boolean)
                                .join(' — ')}
                            </p>
                          )}
                          <p
                            className="text-xs text-muted-foreground mt-1"
                            title={getSignalSummary(match)}
                          >
                            Signals: {getSignalSummary(match)}
                          </p>
                        </div>

                        {/* Actions */}
                        {match.status === 'pending' && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              onClick={() => handleMerge(match)}
                              aria-label="Merge these records"
                            >
                              <GitMerge className="h-4 w-4 mr-1" aria-hidden="true" />
                              Merge
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResolve(match.id, 'not_duplicate')}
                              disabled={resolveMutation.isPending}
                              aria-label="Mark as not a duplicate"
                            >
                              <XCircle className="h-4 w-4 mr-1" aria-hidden="true" />
                              Not a Duplicate
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResolve(match.id, 'dismissed')}
                              disabled={resolveMutation.isPending}
                              aria-label="Dismiss this match"
                            >
                              Dismiss
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  aria-label="Next page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Merge Modal */}
      <MergeRecordsModal
        entityType={mergeEntityType === 'CrossEntity' ? 'Contact' : mergeEntityType}
        recordIdA={mergeRecordA}
        recordIdB={mergeRecordB}
        open={mergeModalOpen}
        onOpenChange={(open) => {
          setMergeModalOpen(open)
          if (!open) {
            setMergeRecordA('')
            setMergeRecordB('')
            setMergeDuplicateId(undefined)
          }
        }}
        duplicateMatchId={mergeDuplicateId}
      />
    </div>
  )
}
